/* ==========================================================================
   Ozeret -- talking to the server

   Every mutation returns the caller's whole world back, and we swap it in
   wholesale. More bytes than a fine-grained API, far fewer ways to drift out
   of sync — the right trade at this size.
   ========================================================================== */

export interface Me {
  userId: string;
  name: string;
  role: 'house' | 'cleaner';
  cleanerId: string | null;
  houseIds: string[];
  isAdmin: boolean;
  /** A look-around: a throwaway copy of the example, never saved. */
  isDemo: boolean;
  /** Shown back pretty, e.g. (631) 933-9716. Null on a look-around. */
  phone: string | null;
  /** This account's permanent key, for signing in on another device. */
  signinCode: string;
  /** Said yes to texts about their own jobs. */
  textsOn?: boolean;
  /** Ozeret can send texts yet (Twilio's number is approved and set). */
  textsReady?: boolean;
}

export interface Bootstrap {
  me: Me | null;
  catalog: Record<string, unknown>;
  cleaners: any[];
  houses: any[];
  requests: any[];
  bookings: any[];
  recurring: any[];
  occurrences: any[];
  availability: Record<string, any>;
  threads: any[];
  reviewsOfCleaners: Record<string, any[]>;
  reviewsOfHouses: Record<string, any[]>;
  /** When each update was last marked read, by the id the app gives it. */
  notifSeen: Record<string, string>;
  /** Notices from Ozeret, written by an admin. */
  notices?: { id: string; text: string; link: string; at: string }[];
  /** Whether codes go by text, or have to be fetched from an admin. */
  smsConfigured: boolean;
  /** Where this account stands: a free month, a subscription, or looking. */
  billing?: {
    standing: 'trial' | 'paid' | 'browsing' | 'free';
    daysLeft: number;
    trialEndsAt: string | null;
    paidUntil: string | null;
    mayAct: boolean;
    /** Cancelled, but still inside the period they paid for. */
    cancelled: boolean;
    /** True for a Stripe subscription that renews; false for free months
     *  from a promo or a referral, which end quietly and cannot be
     *  cancelled because there is nothing to cancel. */
    viaStripe: boolean;
    price: string;
    /** How long the free month is. Present even signed out, which is how the
     *  sign-up screen can say so before there is an account. */
    trialDays: number;
  };
  /** What this profile still needs before it can post or apply. */
  profile?: { ok: boolean; missing: string[]; why: string };
  serverTime: string;
}

export interface HiddenPair {
  houseId: string;
  cleanerId: string;
  /** False when they hid you: you can see it, but you cannot undo it. */
  byMe: boolean;
  at: string;
  name: string;
  initials: string;
  hue: number;
  pendingInvites: number;
}

/** What a photo can be attached to. The server decides who may do which;
 *  this is only the vocabulary. */
export type PhotoKind =
  | 'joblist' | 'booking' | 'thread' | 'profile-house' | 'profile-cleaner';

export interface Photo {
  id: string;
  caption: string;
  width: number;
  height: number;
  at: string;
}

/** Where the bytes for a photo id live. Same origin, so the session cookie
 *  goes with it and the server can check who is asking. */
export const photoUrl = (id: string): string => BASE + '/api/photo/' + id;

export class ApiError extends Error {
  status: number;
  /** The whole refusal, not only its sentence. Some routes say more than the
   *  message does -- that a new number needs a side, that a number holding
   *  two accounts needs one of them named -- and a screen acting on that
   *  should read the field rather than the English. */
  body: Record<string, unknown>;
  constructor(message: string, status: number, body: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

const BASE = import.meta.env.VITE_API_BASE ?? '';

/** Only the page itself knows whether it was launched from a home screen. */
function displayMode(): string {
  try {
    return window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as { standalone?: boolean }).standalone
      ? 'standalone'
      : 'browser';
  } catch {
    return 'browser';
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  const headers: Record<string, string> = { 'x-ozeret-display': displayMode() };
  if (body !== undefined) headers['content-type'] = 'application/json';
  try {
    res = await fetch(BASE + path, {
      method,
      credentials: 'include',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    /* The distinction matters: this is "we could not reach the server", which
       is a different message to the user than "the server said no". */
    throw new ApiError('Cannot reach the server. Check your connection.', 0);
  }

  let data: any = null;
  try { data = await res.json(); } catch { /* empty body */ }

  if (!res.ok) {
    throw new ApiError(
      data?.error ?? 'Something went wrong', res.status,
      data && typeof data === 'object' ? data : {},
    );
  }
  return data as T;
}

const get = <T>(p: string) => call<T>('GET', p);
const post = <T>(p: string, b?: unknown) => call<T>('POST', p, b);
const patch = <T>(p: string, b?: unknown) => call<T>('PATCH', p, b);
const put = <T>(p: string, b?: unknown) => call<T>('PUT', p, b);

type Envelope = { ok: true; state: Bootstrap; id?: string };

/** People who opened the site, from the admin dashboard. */
export interface VisitorReport {
  today: { day: string; visitors: number; members: number; signups: number };
  week: { visitors: number; signups: number };
  days: { day: string; visitors: number; members: number; signups: number }[];
  landings: { name: string; visitors: number; signups: number }[];
  sources: { name: string; visitors: number; signups: number }[];
}

export const api = {
  bootstrap: () => get<Bootstrap>('/api/bootstrap'),

  redeem: (code: string, name: string) =>
    post<Envelope>('/api/auth/redeem', { code, name }),
  signIn: (code: string) => post<Envelope>('/api/auth/signin', { code }),
  /* by phone */
  phoneStart: (phone: string) =>
    post<{
      ok: true;
      delivery: 'sms' | 'admin';
      known: boolean;
      role: string | null;
      /** Every side this number holds. Two of them is an admin's number, and
       *  the screen has to ask which one before anybody can be signed in.
       *  Optional: a tab left open across a deploy may still be talking to a
       *  server that only ever names one. */
      roles?: string[];
    }>('/api/auth/phone/start', { phone }),
  phoneCheck: (b: {
    phone: string; code: string; role?: string | null; name?: string;
    zip?: string; address?: string; areas?: string[]; addressConfirmed?: boolean;
    promo?: string; smsOk?: boolean; lang?: string;
  }) => post<Envelope & {
    created: boolean;
    promo?: { ok: boolean; message: string; months?: number; kind?: string };
  }>('/api/auth/phone/check', b),
  deleteAccount: (reason: string | null, note: string) =>
    post<{ ok: true }>('/api/me/delete', { confirm: 'DELETE', reason, note }),
  adminCodes: () =>
    get<{ smsConfigured: boolean; codes: { phone: string; code: string; expiresAt: string }[] }>(
      '/api/admin/codes',
    ),
  demo: (role: 'house' | 'cleaner') => post<Envelope>('/api/auth/demo', { role }),
  signOut: () => post<{ ok: true }>('/api/auth/signout'),

  /* requests */
  createRequest: (b: unknown) => post<Envelope>('/api/requests', b),
  updateRequest: (id: string, b: unknown) => patch<Envelope>(`/api/requests/${id}`, b),
  closeRequest: (id: string) => post<Envelope>(`/api/requests/${id}/close`),
  apply: (id: string, b: { counterRate?: number; message?: string }) =>
    post<Envelope>(`/api/requests/${id}/apply`, b),
  withdraw: (id: string) => post<Envelope>(`/api/requests/${id}/withdraw`),
  invite: (id: string, cleanerId: string) =>
    post<Envelope>(`/api/requests/${id}/invite`, { cleanerId }),
  choose: (id: string, cleanerId: string) =>
    post<Envelope>(`/api/requests/${id}/choose`, { cleanerId }),

  /* bookings */
  startBooking: (id: string) => post<Envelope>(`/api/bookings/${id}/start`),
  setJobState: (id: string, b: { jobId: string; done?: boolean; note?: string }) =>
    post<Envelope>(`/api/bookings/${id}/job`, b),
  complete: (id: string) => post<Envelope>(`/api/bookings/${id}/complete`),
  cancelBooking: (id: string) => post<Envelope>(`/api/bookings/${id}/cancel`),
  setPaid: (id: string, paid: boolean) => post<Envelope>(`/api/bookings/${id}/paid`, { paid }),
  onceOnly: (id: string, b: { note?: string; jobIds?: string[] }) =>
    post<Envelope>(`/api/bookings/${id}/once`, b),
  review: (id: string, b: unknown) => post<Envelope>(`/api/bookings/${id}/review`, b),
  logPast: (b: unknown) => post<Envelope>('/api/bookings/past', b),

  /* one week of a repeating job */
  setOccurrence: (recurringId: string, b: {
    date: string; jobIds?: string[] | null; note?: string; skipped?: boolean;
  }) => post<Envelope>(`/api/recurring/${recurringId}/occurrence`, b),

  /* where people are */
  zip: (zip: string) =>
    get<{ ok: boolean; zip: string; place?: string; state?: string }>('/api/zip/' + zip),

  /* sharing by link */
  share: (kind: 'request' | 'cleaner' | 'house', id?: string) =>
    post<{ token: string }>('/api/share', { kind, id }),
  readShare: (token: string) => get<any>('/api/share/' + token),

  /* stepping away from somebody */
  hide: (b: { houseId?: string; cleanerId?: string; hide: boolean }) =>
    post<Envelope>('/api/hide', b),
  hidden: () => get<{ hidden: HiddenPair[] }>('/api/hidden'),

  /* availability */
  setDays: (b: unknown) => post<Envelope>('/api/availability/days', b),
  timeOff: (b: unknown) => post<Envelope>('/api/availability/timeoff', b),
  weekly: (weekly: unknown) => post<Envelope>('/api/availability/weekly', { weekly }),
  visibility: (hidden: boolean) => post<Envelope>('/api/availability/visibility', { hidden }),
  removeBlock: (date: string, index: number) =>
    post<Envelope>('/api/availability/removeBlock', { date, index }),

  /* profiles */
  saveCleaner: (b: unknown) => patch<Envelope>('/api/me/cleaner', b),
  saveHouse: (id: string, b: unknown) => patch<Envelope>(`/api/me/house/${id}`, b),
  addHouse: (b: { displayName: string; zip?: string; area?: string }) =>
    post<Envelope & { id: string }>('/api/me/houses', b),
  saveJobList: (id: string, jobList: unknown) =>
    put<Envelope>(`/api/me/house/${id}/joblist`, { jobList }),

  /* messages */
  openThread: (b: { houseId?: string; cleanerId?: string }) =>
    post<Envelope>('/api/threads', b),
  sendMessage: (id: string, text: string, photoIds?: string[]) =>
    post<Envelope>(`/api/threads/${id}/messages`, { text, photoIds }),
  sendOffer: (id: string, rate: number, hours: number, note: string) =>
    post<Envelope>(`/api/threads/${id}/messages`, { kind: 'offer', rate, hours, note }),
  respondToOffer: (id: string, index: number, accept: boolean) =>
    post<Envelope>(`/api/threads/${id}/offer/${index}`, { accept }),
  markRead: (id: string) => post<Envelope>(`/api/threads/${id}/read`),

  /* translating what people wrote */
  translate: (texts: string[], to: string) =>
    post<{ ok: boolean; translations: string[]; reason?: string }>('/api/translate', { texts, to }),
  translateStatus: () =>
    get<{ configured: boolean; languages: string[] }>('/api/translate/status'),

  /* another day for a job nobody answered */
  renewRequest: (id: string, date?: string) =>
    post<Envelope & { date: string }>(`/api/requests/${id}/renew`, { date }),

  /* updates */
  markNotificationsSeen: (items: { id: string; at: string }[]) =>
    post<Envelope>('/api/notifications/seen', { items }),

  /* pictures */
  addPhoto: (b: {
    kind: PhotoKind; ownerId: string; dataUrl: string;
    caption?: string; width?: number; height?: number;
  }) => post<Envelope & { id: string }>('/api/photos', b),
  listPhotos: (kind: PhotoKind, ownerId: string) =>
    get<{ photos: Photo[] }>(
      '/api/photos?kind=' + encodeURIComponent(kind) + '&ownerId=' + encodeURIComponent(ownerId),
    ),
  removePhoto: (id: string) => call<Envelope>('DELETE', `/api/photos/${id}`),

  /* misc */
  feedback: (b: unknown) => post<{ ok: true }>('/api/feedback', b),
  reportCalendar: (b: unknown) => post<{ ok: true }>('/api/reports/calendar', b),
  stats: (span: string) => get<unknown>('/api/admin/stats?span=' + span),
  visitors: () => get<VisitorReport>('/api/admin/visitors'),
  health: () => get<{
    at: string;
    verdict: 'ok' | 'warn' | 'bad' | 'unknown';
    checks: { name: string; level: 'ok' | 'warn' | 'bad' | 'unknown';
      detail: string; fix?: string }[];
  }>('/api/admin/health'),
  mintPromos: (b: { count: number; months: number; note?: string; role?: string | null }) =>
    post<{ ok: true; codes: string[]; months: number }>('/api/admin/promos', b),
  compMonths: (b: { phone: string; months: number }) =>
    post<{ ok: true; name: string; months: number; message: string }>('/api/admin/comp', b),
  checkout: () => post<{ ok: true; url: string }>('/api/billing/checkout', {}),
  /* Named apart from redeem() and share(), which already mean an invite code
     and a "send this to somebody" link. */
  redeemPromo: (code: string) => post<Envelope & {
    ok: true; message: string; months?: number; kind?: string;
  }>('/api/promo/redeem', { code }),
  shareState: () => get<{
    code: string; joined: number; monthsEarned: number;
    monthsLeft: number; perSignup: number;
  }>('/api/share'),
  cancelSubscription: () => post<Envelope>('/api/me/subscription/cancel', {}),
  resumeSubscription: () => post<Envelope>('/api/me/subscription/resume', {}),
  placesNear: (zip: string, within = 25, q = '') => get<{ towns: {
    id: string; town: string; state: string; miles: number }[] }>(
    '/api/places/near?zip=' + encodeURIComponent(zip) + '&within=' + within + (q ? '&q=' + encodeURIComponent(q) : '')),
  tables: () => get<{ tables: { name: string; rows: number;
    columns: { name: string; type: string; pk: boolean }[] }[] }>('/api/admin/tables'),
  query: (sql: string) => post<{ ok: true; columns: string[]; rows: Record<string, unknown>[];
    total: number; truncated: boolean; ms: number }>('/api/admin/query', { sql }),
  pendingCodes: () => get<{ smsConfigured: boolean;
    codes: { phone: string; code: string; expiresAt: string }[] }>('/api/admin/codes'),
  viewAs: (role: 'house' | 'cleaner') => post<Envelope>('/api/me/view-as', { role }),
  /* phone notifications: which kinds, and a test */
  pushPrefs: () => get<{ kinds: string[]; off: string[]; devices: number }>('/api/push/prefs'),
  setPushPrefs: (off: string[]) => put<{ ok: true; off: string[] }>('/api/push/prefs', { off }),
  pushTest: () => post<{ ok: true; result: string | null }>('/api/push/test'),
  setTexts: (on: boolean, via: 'ask' | 'settings', lang: string) =>
    post<Envelope>('/api/me/texts', { on, via, lang }),
};
